import { setMilliseconds, sub } from 'date-fns';
import { createWithEqualityFn as create } from 'zustand/traditional';

import {
  getDefaultInboundSlideoutDimension,
  INBOUND_METRIC_DRILL_TITLES,
  INBOUND_VIEW_BY,
  INBOUND_VIEW_BY_DIMENSION_MAP,
  InboundViewByValue,
} from 'components/insights/careTransitions/inboundReferrals/constants';
import { Dimensions } from 'components/insights/constants';
import { DATES, END_DATE, NONE_OPTION, START_DATE } from 'constants/filterKeysConstants';

import { FilterOption, FilterState } from './types';

const now = setMilliseconds(new Date(), 0);
const thirtyDaysAgo = sub(now, { days: 30 });

export type InboundReferralsInsightsFilterState = FilterState<FilterOption[] | string | Date>;

// The rate tiles that open a metric drill. Derived from INBOUND_METRIC_DRILL_TITLES so the keys
// stay locked to that module structurally rather than by hand.
export type InboundMetricDrillKey = keyof typeof INBOUND_METRIC_DRILL_TITLES;

// A slideout is either an entity drill (clicked a table row) or a metric drill (clicked a rate tile).
export type InboundSlideoutContext =
  | { kind: 'entity'; entityId: string; entityName: string; dimension: Dimensions }
  | { kind: 'metric'; metric: InboundMetricDrillKey };

export type OpenInboundEntitySlideoutArgs = {
  entityId: string;
  entityName: string;
  dimension: Dimensions;
};

type Actions = {
  setViewBy: (viewBy: InboundViewByValue) => void;
  setSelectedFilters: (filters: InboundReferralsInsightsFilterState) => void;
  setExpanded: (expanded: boolean) => void;
  resetFilters: () => void;
  openSlideout: (args: OpenInboundEntitySlideoutArgs) => void;
  openMetricSlideout: (metric: InboundMetricDrillKey) => void;
  closeSlideout: () => void;
  setSlideoutDimension: (dimension: Dimensions) => void;
};

type State = {
  viewBy: InboundViewByValue;
  filters: InboundReferralsInsightsFilterState;
  expanded: boolean;
  slideoutContext: InboundSlideoutContext | null;
  slideoutDimension: Dimensions | null;
  actions: Actions;
};

const defaultFilters = (): InboundReferralsInsightsFilterState => ({
  [START_DATE]: thirtyDaysAgo,
  [END_DATE]: now,
});

export const useInboundReferralsInsightsStore = create<State>((set) => ({
  viewBy: INBOUND_VIEW_BY.RECEIVING_SNF,
  filters: defaultFilters(),
  expanded: false,
  slideoutContext: null,
  slideoutDimension: null,
  actions: {
    setViewBy: (viewBy) => set({ viewBy }),
    setSelectedFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
    setExpanded: (expanded) => set({ expanded }),
    resetFilters: () => set({ viewBy: INBOUND_VIEW_BY.RECEIVING_SNF, filters: defaultFilters() }),
    openSlideout: (args) =>
      set({
        slideoutContext: { kind: 'entity', ...args },
        slideoutDimension: getDefaultInboundSlideoutDimension(args.dimension),
      }),
    openMetricSlideout: (metric) =>
      set((state) => ({
        slideoutContext: { kind: 'metric', metric },
        slideoutDimension: INBOUND_VIEW_BY_DIMENSION_MAP[state.viewBy],
      })),
    closeSlideout: () => set({ slideoutContext: null, slideoutDimension: null }),
    setSlideoutDimension: (dimension) => set({ slideoutDimension: dimension }),
  },
}));

export const useInboundReferralsInsightsActions = () => useInboundReferralsInsightsStore((state) => state.actions);

export const getInboundReferralAnalyticsFilters = ({ filters }: State) => {
  const { [START_DATE]: start, [END_DATE]: end, ...rest } = filters;
  return {
    ...rest,
    [DATES]: [{ start, end }],
  };
};

// Locks slideout queries to the clicked entity. The filter key is the dimension name (the backend
// filters each attribute by its own name). Facility filters by group id; referral_source /
// payer_source filter by their text value (which is also the entityId). Metric drills add no entity
// filter — they scope only by the dashboard's active filters.
export const getInboundSlideoutEntityFilter = (
  context: InboundSlideoutContext
): Record<string, { id: string; name: string }[]> | null => {
  if (context.kind !== 'entity') return null;
  const key = context.dimension;
  // 'null'/'None' is the API sentinel for IS NULL; the filter param must be an array or strong
  // params strips a scalar.
  if (!context.entityId || context.entityId === 'None') {
    return { [key]: [NONE_OPTION] };
  }
  return { [key]: [{ id: context.entityId, name: context.entityName }] };
};
